/**
 * HERE returns ISO 3166-1 alpha-3 country codes, but tbl_GL_Country.IsoCode is alpha-2.
 * Covers the countries Adora operates in — add a line when a new one is onboarded.
 */
const ALPHA3_TO_ALPHA2: Record<string, string> = {
    BHS: "BS",
    CAN: "CA",
    ESP: "ES",
    GBR: "GB",
    MEX: "MX",
    PRI: "PR",
    USA: "US"
};

const DIRECTION_ABBREVIATIONS: Record<string, string> = {
    north: "N",
    south: "S",
    east: "E",
    west: "W",
    northeast: "NE",
    northwest: "NW",
    southeast: "SE",
    southwest: "SW"
};

const SUFFIX_ABBREVIATIONS: Record<string, string> = {
    alley: "Aly",
    avenue: "Ave",
    beach: "Bch",
    bend: "Bnd",
    boulevard: "Blvd",
    bridge: "Brg",
    brook: "Brk",
    bypass: "Byp",
    canyon: "Cyn",
    causeway: "Cswy",
    center: "Ctr",
    circle: "Cir",
    cliff: "Clf",
    club: "Clb",
    common: "Cmn",
    corner: "Cor",
    course: "Crse",
    court: "Ct",
    cove: "Cv",
    creek: "Crk",
    crescent: "Cres",
    crest: "Crst",
    crossing: "Xing",
    dale: "Dl",
    drive: "Dr",
    estate: "Est",
    estates: "Ests",
    expressway: "Expy",
    extension: "Ext",
    falls: "Fls",
    ferry: "Fry",
    field: "Fld",
    fields: "Flds",
    forest: "Frst",
    fork: "Frk",
    fort: "Ft",
    freeway: "Fwy",
    garden: "Gdn",
    gardens: "Gdns",
    gateway: "Gtwy",
    glen: "Gln",
    green: "Grn",
    grove: "Grv",
    harbor: "Hbr",
    haven: "Hvn",
    heights: "Hts",
    highway: "Hwy",
    hill: "Hl",
    hills: "Hls",
    hollow: "Holw",
    inlet: "Inlt",
    island: "Is",
    junction: "Jct",
    knoll: "Knl",
    lake: "Lk",
    lakes: "Lks",
    landing: "Lndg",
    lane: "Ln",
    lodge: "Ldg",
    manor: "Mnr",
    meadow: "Mdw",
    meadows: "Mdws",
    mill: "Ml",
    mission: "Msn",
    mount: "Mt",
    mountain: "Mtn",
    orchard: "Orch",
    parkway: "Pkwy",
    passage: "Psge",
    place: "Pl",
    plain: "Pln",
    plaza: "Plz",
    point: "Pt",
    port: "Prt",
    prairie: "Pr",
    ranch: "Rnch",
    rapids: "Rpds",
    ridge: "Rdg",
    river: "Riv",
    road: "Rd",
    route: "Rte",
    shore: "Shr",
    skyway: "Skwy",
    spring: "Spg",
    springs: "Spgs",
    square: "Sq",
    station: "Sta",
    stream: "Strm",
    street: "St",
    summit: "Smt",
    terrace: "Ter",
    trace: "Trce",
    track: "Trak",
    trail: "Trl",
    tunnel: "Tunl",
    turnpike: "Tpke",
    valley: "Vly",
    viaduct: "Via",
    view: "Vw",
    village: "Vlg",
    vista: "Vis"
};

const ALPHA2_TO_ALPHA3: Record<string, string> = Object.fromEntries(
    Object.entries(ALPHA3_TO_ALPHA2).map(([alpha3, alpha2]) => [alpha2, alpha3])
);

export function toAlpha2CountryCode(countryCode?: string): string | undefined {
    if (!countryCode) return undefined;

    const normalized = countryCode.trim().toUpperCase();
    return ALPHA3_TO_ALPHA2[normalized] ?? countryCode;
}

/**
 * HERE's `in=countryCode:` filter takes uppercase alpha-3 codes. A code with no mapping is
 * dropped rather than passed through, so an unknown country widens the search instead of
 * making HERE reject the whole request.
 */
export function toAlpha3CountryCodes(countryCodes: readonly string[]): string[] {
    const mapped = countryCodes
        .map(code => ALPHA2_TO_ALPHA3[code?.trim().toUpperCase()])
        .filter((code): code is string => Boolean(code));

    return Array.from(new Set(mapped));
}

/**
 * HERE spells street types out ("North Howe Avenue") while Google's short_name abbreviates
 * them ("N Howe Ave"). Downstream street-name comparisons expect the abbreviated form.
 */
export function toStreetShortName(street?: string): string | undefined {
    if (!street) return street;

    const words = street.trim().split(/\s+/).filter(Boolean);
    if (words.length < 2) return street;

    let suffixIndex = words.length - 1;

    const leadingDirection = DIRECTION_ABBREVIATIONS[words[0].toLowerCase()];
    if (leadingDirection)
        words[0] = leadingDirection;

    // A trailing direction pushes the street type one word left: "Main Street North"
    const trailingDirection = DIRECTION_ABBREVIATIONS[words[suffixIndex].toLowerCase()];
    if (trailingDirection && suffixIndex > 0) {
        words[suffixIndex] = trailingDirection;
        suffixIndex--;
    }

    // Guard the leading word so a lone "Park Avenue" keeps its name
    if (suffixIndex > 0)
        words[suffixIndex] = SUFFIX_ABBREVIATIONS[words[suffixIndex].toLowerCase()] ?? words[suffixIndex];

    return words.join(" ");
}
